import BorrowingResponsibilityConfirmation from "@/components/borrowing/BorrowingResponsibilityConfirmation";
import SelectionFilter from "@/components/multi-select-filter/SelectionFilter";
import { Button } from "@/components/ui/button/Button";
import { Calendar } from "@/components/ui/calendar/Calendar";
import { Checkbox } from "@/components/ui/checkbox/Checkbox";
import { Input } from "@/components/ui/input/Input";
import { Label } from "@/components/ui/label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/Select";
import {
  LOKASI_ANGGREK,
  LOKASI_BEKASI,
  RUANG_LAB_FOODTECH_ALSUT,
  RUANG_LAB_KOMPUTER_ALSUT,
} from "@/lib/constants/constants";
import { useToast } from "@/lib/hooks/useToast";
import BorrowingService from "@/lib/services/borrowing/BorrowingService";
import RoomService from "@/lib/services/room/RoomService";
import RoomLocationService from "@/lib/services/room/RoomLocationService";
import ShiftService from "@/lib/services/shift/ShiftService";
import {
  convertShiftsToOptions,
  resolveBorrowerCredentials,
} from "@/lib/utils";
import { UserFacingError } from "@/models/helper/UserFacingError";
import { IRoom } from "@/models/room/Room";
import { IRoomLocation } from "@/models/room/RoomLocation";
import { IShift } from "@/models/shift/Shift";
import { AnimatePresence, motion } from "framer-motion";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Matcher, isMatch } from "react-day-picker";
import { FaPlus, FaX } from "react-icons/fa6";
import ChooseAssetsDialog, { FetchAssetsParams } from "./ChooseAssetsDialog";
import IAsset from "@/models/asset/Asset";
import { PaginatedResponse } from "@/models/helper/Response";
import { Badge } from "@/components/ui/badge/Badge";
import { BookOpenCheck } from "lucide-react";
import { AvailableShiftsResponse } from "@/models/shift/AvailableShiftsResponse";

type InputListItem = {
  id: number;
  value: string;
};

const initialFormData = {
  locationId: null as number | null,
  room: null as IRoom | null,
  date: undefined as Date | undefined,
  shifts: [] as IShift[],
  purpose: "",
  borrowerPhoneNumber: "",
  borrowingReasonCategory: "",
  needInternet: false,
  needComputer: false, // ADDED
  pcCount: 0, // ADDED
  needChemicals: false,
  leaderEmailOrBinusianId: "",
  membersEmailOrBinusianId: [] as InputListItem[],
  assets: [] as IAsset[],
  isPrivate: false,
  isResponsibilityConfirmed: false,
};

type Props = {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

function InsertRoomBorrowingForm({ setLoading }: Props) {
  const { toast } = useToast();
  const [locations, setLocations] = useState<IRoomLocation[]>([]);
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [shiftsResponse, setShiftsResponse] = useState<AvailableShiftsResponse>(
    { shifts: [], room_capacity: 0 },
  );
  const [holidays, setHolidays] = useState<Matcher[]>([{ dayOfWeek: [0] }]);
  const [roomHaveAsset, setRoomHaveAsset] = useState(false);
  const [changeAssetsDialogOpen, setChangeAssetsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    const fetchLocations = async () => {
      setLocations(await RoomLocationService.getBorrowingLocations());
    };

    const fetchHolidays = async () => {
      const holidays: Date[] = await ShiftService.getAllHoliday(
        new Date().getFullYear().toString(),
      );

      setHolidays((prev) => [...prev, ...holidays]);
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchLocations(), fetchHolidays()]);
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

  useEffect(() => {
    const fetchRooms = async () => {
      if (formData.locationId === null) return;
      setLoading(true);
      setRooms(await RoomService.getBorrowingRooms(formData.locationId));
      setLoading(false);
    };

    setFormData((prev) => ({ ...prev, room: null }));
    fetchRooms();
  }, [formData.locationId, setLoading]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      assets: [],
      date: undefined,
      shifts: [],
      isPrivate:
        formData.room?.room_privacy_category === "Private" ? true : false,
    }));
  }, [formData.room]);

  useEffect(() => {
    const fetchShifts = async () => {
      if (formData.date === undefined) return;
      const chosenRoom = formData.room;
      if (!chosenRoom) return;

      setLoading(true);
      const rawShiftsResponse = await ShiftService.getAvailableShifts(
        formData.date,
        chosenRoom.id.toString(),
        chosenRoom.room_code,
      );

      setShiftsResponse({
        ...rawShiftsResponse,
        shifts: rawShiftsResponse.shifts.filter(
          (shift) => shift.borrower_count < rawShiftsResponse.room_capacity,
        ),
      });
      setLoading(false);
    };

    fetchShifts();
  }, [formData.date, formData.room, rooms, setLoading]);

  const fetchAssets = useCallback(
    async ({
      room,
      currentPage,
      searchTerm,
    }: FetchAssetsParams): Promise<PaginatedResponse<IAsset[]>> => {
      if (room === null)
        return { data: [], current_page: 1, last_page: 1, per_page: 15 };

      setLoading(true);
      const res = await BorrowingService.getAssetByRooms(
        [room.id.toString()],
        currentPage,
        searchTerm.trim() ? searchTerm.trim() : undefined,
      );

      setRoomHaveAsset(res.data.length > 0);
      setLoading(false);
      return res;
    },
    [setLoading],
  );

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const addMember = () => {
    const newId =
      formData.membersEmailOrBinusianId.length === 0
        ? 1
        : formData.membersEmailOrBinusianId[
          formData.membersEmailOrBinusianId.length - 1
        ].id + 1;
    setFormData((prev) => ({
      ...prev,
      membersEmailOrBinusianId: [
        ...prev.membersEmailOrBinusianId,
        { id: newId, value: "" },
      ],
    }));
  };

  const removeMember = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      membersEmailOrBinusianId: prev.membersEmailOrBinusianId.filter(
        (m) => m.id !== id,
      ),
    }));
  };

  const handleInputMember = (id: number, e: ChangeEvent<HTMLInputElement>) => {
    const newMembers = formData.membersEmailOrBinusianId.map((mem) =>
      mem.id === id ? { ...mem, value: e.target.value } : mem,
    );
    setFormData({
      ...formData,
      membersEmailOrBinusianId: newMembers,
    });
  };

  const validateForm = (): boolean => {
    if (
      !formData.locationId ||
      !formData.room ||
      !formData.date ||
      formData.shifts.length === 0 ||
      !formData.borrowingReasonCategory.trim() ||
      !formData.purpose.trim() ||
      !formData.borrowerPhoneNumber.trim() ||
      isNaN(+formData.borrowerPhoneNumber) ||
      !formData.leaderEmailOrBinusianId.trim() ||
      !formData.isResponsibilityConfirmed
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    if (!validateForm()) {
      toast({
        title: "Form error",
        description: "Please fill all required fields...",
      });
      setLoading(false);
      return;
    }

    try {
      // fetch leader binusian data
      const leader = await resolveBorrowerCredentials([
        formData.leaderEmailOrBinusianId,
      ]);

      // fetch members binusian data
      const credentials = formData.membersEmailOrBinusianId.map((m) => m.value);
      const teamMembers = await resolveBorrowerCredentials(credentials);

      const studentNimLeader = leader[0].binusian_id;
      const borrowerEmail = leader[0].email;

      // submit
      const res = await BorrowingService.submitBorrowing(
        "staff",
        formData.date!,
        formData.room!.id,
        formData.shifts.map((s) => Number(s.id)),
        formData.borrowingReasonCategory,
        formData.purpose,
        formData.needInternet,
        studentNimLeader,
        borrowerEmail,
        formData.borrowerPhoneNumber,
        formData.isPrivate,
        formData.needChemicals,
        teamMembers,
        formData.assets,
        formData.pcCount // ADDED THIS
      );

      resetForm();
      toast({
        title: "Success.",
        description: res.message,
      });
    } catch (error) {
      if (error instanceof UserFacingError) {
        toast({
          title: error.title,
          description: error.description,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <ChooseAssetsDialog
        open={changeAssetsDialogOpen}
        setIsOpen={setChangeAssetsDialogOpen}
        room={formData.room}
        savedAssets={formData.assets}
        onSaveAssets={(assets) => {
          setFormData({ ...formData, assets: assets });
        }}
        fetchAssetsFunction={fetchAssets}
      />
      <form
        className="w-full rounded-lg flex flex-col items-center border-primary bg-background py-8 xl:p-16"
        onSubmit={handleSubmit}
      >
        <div className=" mb-4 flex w-full items-center justify-center pb-5 flex-col gap-3 border-b-2 ">
          <h3 className="text-3xl text-center text-primary xl:text-5xl">
            Room Borrowing
          </h3>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Campus <span className="text-red-500 text-lg">*</span>
          </Label>
          <Select
            onValueChange={(value) => {
              setFormData({ ...formData, locationId: Number(value) });
            }}
          >
            <SelectTrigger className="w-full text-md">
              <SelectValue placeholder="Select Campus" />
            </SelectTrigger>
            <SelectContent>
              {locations.length === 0 && <div>No Data</div>}
              {locations.map((location) => (
                <SelectItem key={location.id} value={location.id.toString()}>
                  {location.location}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Room <span className="text-red-500 text-lg">*</span>
          </Label>
          <Select
            onValueChange={(value) => {
              setFormData({
                ...formData,
                room: rooms.find((r) => r.id === Number(value)) || null,
              });
            }}
            disabled={formData.locationId === null}
          >
            <SelectTrigger className="w-full text-md">
              <SelectValue placeholder="Select Room" />
            </SelectTrigger>
            <SelectContent>
              {rooms.length === 0 && <div>No Data</div>}
              {rooms.map((room) => (
                <SelectItem key={room.id} value={room.id.toString()}>
                  {room.room_code} - {room.room_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-full gap-5 rounded-md pb-3 mb-3 xl:text-lg">
          <div className="flex flex-col gap-3">
            <Label className="font-bold text-md">
              Date <span className="text-red-500 text-lg">*</span>
            </Label>
            <div className="w-full min-h-72 flex flex-col lg:flex-row gap-4">
              <div className="flex flex-col">
                <Calendar
                  className="self-center"
                  mode="single"
                  captionLayout="dropdown-buttons"
                  selected={formData.date}
                  onSelect={(date) => {
                    setFormData({ ...formData, date: date, shifts: [] });
                  }}
                  disabled={formData.room === null}
                  fromYear={new Date().getFullYear()}
                  toYear={new Date().getFullYear() + 2}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Label className="font-bold text-md">
              Shifts <span className="text-red-500 text-lg">*</span>
            </Label>
            <h4
              className={`text-sm ${formData.date === undefined ? "block" : "hidden"
                }`}
            >
              Please select a date before selecting shift.
            </h4>
            <div
              className={`${formData.date === undefined ? "hidden" : "block"}`}
            >
              <h4
                className={`text-sm text-yellow-300 mb-2 ${formData.date && isMatch(formData.date, holidays)
                    ? "block"
                    : "hidden"
                  }`}
              >
                Warning: you are selecting a holiday, if this is unintended,
                please select another date
              </h4>
              {shiftsResponse.shifts.length < 1 ? (
                <>No shifts available</>
              ) : (
                <SelectionFilter
                  multiSelect
                  options={convertShiftsToOptions(shiftsResponse.shifts)}
                  handleSelected={(selected) => {
                    setFormData({
                      ...formData,
                      shifts: shiftsResponse.shifts.filter((s) =>
                        selected.some((sel) => sel.id === s.id.toString()),
                      ),
                    });
                  }}
                  selected={convertShiftsToOptions(formData.shifts)}
                  classNames={{ list: "md:grid-cols-2" }}
                  renderLabel={(o) => (
                    <div className="flex flex-col">
                      <p>{o.name}</p>
                      <p>
                        Booked:{" "}
                        {shiftsResponse.shifts.find(
                          (s) => s.id.toString() === o.id,
                        )?.borrower_count || 0}
                        /{shiftsResponse.room_capacity}
                      </p>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">Assets</Label>
          <div className="flex w-full gap-4">
            <div className="flex flex-wrap gap-2 w-full text-md border border-input bg-background px-3 py-2 rounded-md">
              {formData.assets.length === 0 ? (
                <>No assets selected</>
              ) : (
                formData.assets.map((asset) => (
                  <Badge className="text-sm" variant={"secondary"}>
                    {asset.id} - {asset.name}
                  </Badge>
                ))
              )}
            </div>
            <Button
              type="button"
              variant={"blue"}
              className="w-60 flex items-center gap-4"
              onClick={() => {
                setChangeAssetsDialogOpen(true);
              }}
              disabled={formData.room === null || !roomHaveAsset}
            >
              Choose Assets <BookOpenCheck />
            </Button>
          </div>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label>
            Borrowing Reason Category{" "}
            <span className="text-red-500 text-lg">*</span>
          </Label>

          <select
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            value={formData.borrowingReasonCategory}
            onChange={(e) =>
              setFormData({
                ...formData,
                borrowingReasonCategory: e.target.value,
              })
            }
          >
            <option value="">Select Borrowing Reason Category</option>
            <option value="Extra class / Make Up Class">
              Extra class / Make Up Class
            </option>
            <option value="Event">Event</option>
            <option value="Meeting">Meeting</option>
            <option value="Self Learning">Self Learning</option>
            <option value="Seminar">Seminar</option>
            <option value="Training / Workshop">Training / Workshop</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Notes <span className="text-red-500 text-lg">*</span>
          </Label>
          <Input
            value={formData.purpose}
            onChange={(e) =>
              setFormData({ ...formData, purpose: e.target.value })
            }
            type="text"
            placeholder="Borrowing Purpose"
            className="w-full text-md"
            disabled={formData.date === undefined}
          />
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Phone <span className="text-red-500 text-lg">*</span>
          </Label>
          <Input
            value={formData.borrowerPhoneNumber}
            onChange={(e) =>
              setFormData({ ...formData, borrowerPhoneNumber: e.target.value })
            }
            type="tel"
            placeholder="Phone Number"
            className="w-full text-md"
            disabled={formData.date === undefined}
          />
        </div>
        {(formData.room?.type?.id === RUANG_LAB_KOMPUTER_ALSUT ||
          formData.locationId === LOKASI_ANGGREK ||
          formData.locationId === LOKASI_BEKASI) && (
            <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
              <Label className="font-bold text-md">Use Internet?</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="needInternet"
                  checked={formData.needInternet ?? false}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, needInternet: checked === true })
                  }
                  disabled={formData.date === undefined}
                />
                <Label
                  htmlFor="needInternet"
                  className="text-md font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Yes
                </Label>
              </div>

              {/* ADDED NEED COMPUTER UI */}
              <Label className="font-bold text-md mt-2">Need Computer?</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="needComputer"
                  checked={formData.needComputer ?? false}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      needComputer: checked === true,
                      pcCount: checked ? formData.pcCount : 0
                    })
                  }
                  disabled={formData.date === undefined}
                />
                <Label
                  htmlFor="needComputer"
                  className="text-md font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Yes
                </Label>
              </div>

              {formData.needComputer && (
                <div className="w-full mt-1">
                  <Label className="text-sm text-gray-600 block mb-1">How many computers?</Label>
                  <Input
                    type="number"
                    min={1}
                    value={formData.pcCount || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, pcCount: parseInt(e.target.value) || 0 })
                    }
                    placeholder="Enter PC count"
                    className="w-full text-md"
                    disabled={formData.date === undefined}
                  />
                </div>
              )}
            </div>
          )}
        {formData.room?.type?.id === RUANG_LAB_FOODTECH_ALSUT && (
          <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
            <Label className="font-bold text-md">Request Material?</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="needChemicals"
                checked={formData.needChemicals ?? false}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, needChemicals: checked === true })
                }
                disabled={formData.date === undefined}
              />
              <Label
                htmlFor="needChemicals"
                className="text-md font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Yes
              </Label>
            </div>
          </div>
        )}
        {formData.room?.room_privacy_category === "Mixed" && (
          <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
            <Label className="font-bold text-md">Private Borrowing?</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isPrivate"
                checked={formData.isPrivate ?? false}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isPrivate: checked === true })
                }
                disabled={formData.date === undefined}
              />
              <Label
                htmlFor="isPrivate"
                className="text-md font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Yes
              </Label>
            </div>
          </div>
        )}
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Leader <span className="text-red-500 text-lg">*</span>
          </Label>
          <div className="flex w-full gap-4">
            <Input
              value={formData.leaderEmailOrBinusianId}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  leaderEmailOrBinusianId: e.target.value,
                })
              }
              placeholder="Student Email / Binusian ID"
              className="w-full text-md"
              disabled={formData.date === undefined}
            />
            <Button
              type="button"
              onClick={addMember}
              variant={"blue"}
              className="w-60 flex items-center gap-4"
              disabled={formData.date === undefined}
            >
              Add Member <FaPlus />
            </Button>
          </div>
        </div>
        <div className="flex flex-col w-full gap-2 max-h-48 overflow-y-auto px-6 pb-3 mb-3">
          <AnimatePresence>
            {formData.membersEmailOrBinusianId.map((m, idx) => (
              <motion.div
                key={idx}
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  type: "just",
                  ease: "linear",
                  duration: 0.5,
                }}
              >
                <Label htmlFor={`team${m.id}`} className="text-md">
                  Member {idx + 1}
                </Label>
                <div className="flex w-full items-center space-x-2">
                  <Input
                    id={`team${m.id}`}
                    placeholder="Member Email / Binusian ID"
                    className="flex-1 text-md"
                    onChange={(e) => handleInputMember(m.id, e)}
                    value={m.value}
                  />
                  <button
                    title="Remove Member"
                    className="w-auto"
                    type="button"
                  >
                    <FaX
                      onClick={() => removeMember(m.id)}
                      className="text-red-600 hover:text-red-600/75 cursor-pointer transition-colors ease-linear duration-200"
                    />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        <BorrowingResponsibilityConfirmation
          checked={formData.isResponsibilityConfirmed}
          onCheckedChange={(checked) =>
            setFormData({
              ...formData,
              isResponsibilityConfirmed: checked,
            })
          }
          className="mb-6"
        />
        <Button
          type="submit"
          variant={"blue"}
          className="w-60"
          disabled={!validateForm()}
        >
          Submit
        </Button>
      </form>
    </motion.div>
  );
}

export default InsertRoomBorrowingForm;
